import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Hook for mobile drag functionality
 * @param {Object} options Configuration options
 * @param {Function} options.onDrop - Callback when item is dropped
 * @param {Function} options.onTap - Callback for quick tap
 * @param {Object} options.dragData - Data to pass on drop
 * @param {string} options.dragImageUrl - URL for drag ghost image
 * @param {string} options.dragText - Text for drag ghost
 * @param {number} options.longPressDelay - Delay before drag starts (default 300ms)
 * @param {number} options.scrollThreshold - Movement threshold for scroll detection (default 5px)
 * @param {number} options.tapDuration - Max duration for tap (default 300ms)
 * @param {boolean} options.enabled - Whether drag is enabled (default true)
 */
export const useMobileDrag = (options = {}) => {
  const {
    onDrop,
    onTap,
    dragData = {},
    dragImageUrl = '',
    dragText = '',
    longPressDelay = 300,
    scrollThreshold = 5,
    tapDuration = 300,
    enabled = true
  } = options;

  // State
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });

  // Refs
  const touchStartDataRef = useRef(null);
  const isScrollingRef = useRef(false);
  const longPressTimerRef = useRef(null);
  const dragGhostRef = useRef(null);
  const elementRef = useRef(null);

  // Create/update drag ghost element
  useEffect(() => {
    if (!enabled) return;

    if (isDragging && !dragGhostRef.current) {
      const ghost = document.createElement('div');
      ghost.className = 'mobile-drag-ghost';
      ghost.innerHTML = `
        ${dragImageUrl ? `<img src="${dragImageUrl}" alt="" />` : ''}
        ${dragText ? `<div class="drag-ghost-text">${dragText}</div>` : ''}
      `;
      ghost.style.cssText = `
        position: fixed;
        pointer-events: none;
        z-index: 9999;
        opacity: 0.8;
        background: white;
        border: 2px solid #2196F3;
        border-radius: 4px;
        padding: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        transform: translate(-50%, -50%);
        max-width: 150px;
        text-align: center;
      `;

      if (dragImageUrl) {
        const img = ghost.querySelector('img');
        if (img) {
          img.style.cssText = `
            width: 100%;
            height: auto;
            max-height: 100px;
            object-fit: contain;
          `;
        }
      }

      document.body.appendChild(ghost);
      dragGhostRef.current = ghost;

      // Prevent body scroll while dragging
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }

    // Update ghost position
    if (dragGhostRef.current) {
      dragGhostRef.current.style.left = `${dragPosition.x}px`;
      dragGhostRef.current.style.top = `${dragPosition.y}px`;
    }

    // Cleanup
    return () => {
      if (dragGhostRef.current && !isDragging) {
        dragGhostRef.current.remove();
        dragGhostRef.current = null;
        // Restore body scroll
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
    };
  }, [isDragging, dragPosition, dragImageUrl, dragText, enabled]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      if (dragGhostRef.current) {
        dragGhostRef.current.remove();
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
    };
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;

    const touch = e.touches[0];
    touchStartDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now()
    };
    isScrollingRef.current = false;

    // Start long press detection
    longPressTimerRef.current = setTimeout(() => {
      if (!isScrollingRef.current) {
        // Vibrate if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        // Start dragging
        setIsDragging(true);
        setDragPosition({ x: touch.clientX, y: touch.clientY });
      }
    }, longPressDelay);
  }, [enabled, longPressDelay]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStartDataRef.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartDataRef.current.startX);
    const deltaY = Math.abs(touch.clientY - touchStartDataRef.current.startY);

    // Detect scroll or drag
    if (deltaX > scrollThreshold || deltaY > scrollThreshold) {
      if (isDragging) {
        // Dragging - update position (don't prevent default here)
        setDragPosition({ x: touch.clientX, y: touch.clientY });
      } else {
        // Scrolling - cancel drag
        isScrollingRef.current = true;
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }
    }
  }, [enabled, isDragging, scrollThreshold]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled) return;

    console.log('Touch end - isDragging:', isDragging);

    // Clear timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (!touchStartDataRef.current) return;

    const duration = Date.now() - touchStartDataRef.current.startTime;
    const touch = e.changedTouches[0];

    console.log('Touch end - duration:', duration, 'isScrolling:', isScrollingRef.current);

    if (isDragging) {
      // Handle drop
      console.log('Handling drop at:', touch.clientX, touch.clientY);
      if (onDrop) {
        onDrop({
          x: touch.clientX,
          y: touch.clientY,
          data: dragData,
          event: e
        });
      }

      setIsDragging(false);
    } else if (!isScrollingRef.current && duration < tapDuration) {
      // Quick tap
      console.log('Handling tap');
      if (onTap) {
        onTap({
          x: touch.clientX,
          y: touch.clientY,
          data: dragData,
          event: e
        });
      }
    }

    // Reset state
    touchStartDataRef.current = null;
    isScrollingRef.current = false;
  }, [enabled, isDragging, onDrop, onTap, dragData, tapDuration]);

  // Set up non-passive touch move listener when dragging
  useEffect(() => {
    if (!enabled || !elementRef.current) return;

    const element = elementRef.current;

    const handleTouchMoveNonPassive = (e) => {
      if (isDragging && touchStartDataRef.current) {
        // Prevent scrolling while dragging
        e.preventDefault();
        const touch = e.touches[0];
        setDragPosition({ x: touch.clientX, y: touch.clientY });
      }
    };

    if (isDragging) {
      // Add non-passive listener
      element.addEventListener('touchmove', handleTouchMoveNonPassive, { passive: false });

      return () => {
        element.removeEventListener('touchmove', handleTouchMoveNonPassive);
      };
    }
  }, [enabled, isDragging]);

  return {
    isDragging,
    elementRef, // Return ref to be attached to element
    handlers: enabled ? {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    } : {},
    dragStyle: {
      opacity: isDragging ? 0.5 : 1,
      transform: isDragging ? 'scale(0.95)' : 'scale(1)',
      transition: 'opacity 0.2s, transform 0.2s'
    }
  };
};
